import { celsiusToDisplay, displayToCelsius } from "./thermostat-state"
import type { TemperatureUnit, ThermostatSetPointLimits } from "./thermostat-state"

export type NestDisplayState = "awake" | "farsight" | "asleep"
export type NestFarsightFace = "target" | "room" | "clock"

/** Local preview limits; a host adapter can supply the device's actual capabilities. */
export const NEST_LEARNING_LIMITS: ThermostatSetPointLimits = {
  minCelsius: 10,
  maxCelsius: displayToCelsius(90, "fahrenheit"),
  minHeatCoolDeltaCelsius: 5 / 3,
}

/** The reference display lays its ticks over a 300° arc, open at the bottom. */
export function nestTemperatureAngle(celsius: number, limits = NEST_LEARNING_LIMITS) {
  const span = limits.maxCelsius - limits.minCelsius
  if (span <= 0 || !Number.isFinite(celsius)) {
    return 0
  }
  const fraction = (celsius - limits.minCelsius) / span
  return -150 + Math.max(0, Math.min(1, fraction)) * 300
}

/** Signed incremental motion keeps dragging across twelve o'clock continuous. */
export function nestAngleDelta(previous: number, next: number) {
  return ((next - previous + 540) % 360) - 180
}

export function nestPoint(angle: number, radius: number) {
  const radians = angle * Math.PI / 180
  return { x: 200 + Math.sin(radians) * radius, y: 200 - Math.cos(radians) * radius }
}

/** Quantize on the visible unit grid, with bounds rounded inward to preserve Auto's deadband. */
export function nestRingSetPoint(
  startCelsius: number,
  rotation: number,
  unit: TemperatureUnit,
  bounds: { minCelsius: number, maxCelsius: number },
  limits = NEST_LEARNING_LIMITS,
) {
  const step = unit === "fahrenheit" ? 1 : 0.5
  const candidate = celsiusToDisplay(startCelsius + rotation / 300 * (limits.maxCelsius - limits.minCelsius), unit)
  const min = Math.ceil((celsiusToDisplay(bounds.minCelsius, unit) - 1e-8) / step) * step
  const max = Math.floor((celsiusToDisplay(bounds.maxCelsius, unit) + 1e-8) / step) * step
  if (min > max || !Number.isFinite(candidate)) {
    return startCelsius
  }
  return displayToCelsius(Math.max(min, Math.min(max, Math.round(candidate / step) * step)), unit)
}
